import type { FC } from 'react';
import type { SpecialData } from './DirectSpecialCard';

interface IntroBulletsSpecialCardProps {
  special: SpecialData;
}

/**
 * Formats text with intro paragraph and bullet points
 * Detects and properly formats:
 * - Title/header
 * - Introductory paragraph
 * - Bullet points
 * - Call to action
 */
const formatIntroBulletsText = (text: string): JSX.Element[] => {
  if (!text) return [<p key="empty">No content available</p>];

  // Split the text into lines
  const lines = text.split('\n').filter(line => line.trim() !== '');
  const formattedElements: JSX.Element[] = [];

  let currentIndex = 0;
  let hasProcessedTitle = false;
  let hasProcessedIntro = false;

  // Process the title (first non-empty line)
  if (lines.length > 0 && !hasProcessedTitle) {
    formattedElements.push(
      <h2 key={`title-${currentIndex}`} className="text-xl font-bold mb-3 text-center">
        {lines[currentIndex]}
      </h2>
    );
    currentIndex++;
    hasProcessedTitle = true;
  }

  // Process the intro paragraph (text before bullet points)
  const introParagraphs: string[] = [];
  while (
    currentIndex < lines.length && 
    !lines[currentIndex].trim().startsWith('•') && 
    !lines[currentIndex].trim().startsWith('-') &&
    !hasProcessedIntro
  ) {
    introParagraphs.push(lines[currentIndex]);
    currentIndex++;
  }

  if (introParagraphs.length > 0) {
    formattedElements.push(
      <p key={`intro-${currentIndex}`} className="text-gray-700 mb-4 leading-relaxed">
        {introParagraphs.join(' ')}
      </p>
    );
    hasProcessedIntro = true;
  }

  // Process bullet points
  const bulletPoints: string[] = [];
  while (
    currentIndex < lines.length && 
    (lines[currentIndex].trim().startsWith('•') || lines[currentIndex].trim().startsWith('-'))
  ) {
    bulletPoints.push(lines[currentIndex].trim());
    currentIndex++;
  }

  if (bulletPoints.length > 0) {
    formattedElements.push(
      <ul key="bullet-list" className="list-disc pl-6 mb-4 space-y-2">
        {bulletPoints.map((point, idx) => (
          <li key={`bullet-${idx}`} className="text-gray-700">
            {point.replace(/^[•-]\s*/, '')}
          </li>
        ))}
      </ul>
    );
  }

  // Process call to action or remaining content
  const remainingLines: string[] = [];
  while (currentIndex < lines.length) {
    remainingLines.push(lines[currentIndex]);
    currentIndex++;
  }

  if (remainingLines.length > 0) {
    formattedElements.push(
      <p key="call-to-action" className="text-gray-700 italic mt-2">
        {remainingLines.join(' ')}
      </p>
    );
  }

  return formattedElements;
};

export const IntroBulletsSpecialCard: FC<IntroBulletsSpecialCardProps> = ({ special }) => {
  console.log('Rendering IntroBulletsSpecialCard with data:', special);

  return (
    <div className="max-w-md mx-auto px-4 my-6">
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {special.logoUrl && (
          <div className="flex justify-center p-4 bg-gray-50">
            <img
              src={special.logoUrl}
              alt="Restaurant Logo"
              className="h-12 object-contain"
              loading="lazy"
            />
          </div>
        )}

        {special.imageUrl && (
          <img
            src={special.imageUrl}
            alt={special.name}
            className="w-full h-48 sm:h-64 object-cover"
            loading="lazy"
          />
        )}

        <div className="p-6">
          {/* Only show name as title if it's not already in the description */}
          {!special.description.includes(special.name) && (
            <h2 className="text-xl sm:text-2xl font-bold text-center mb-3">{special.name}</h2>
          )}
          
          <div className="text-gray-700 text-sm sm:text-base leading-relaxed">
            {formatIntroBulletsText(special.description)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntroBulletsSpecialCard;
